import { useState } from 'react';
import { Alert, Button, Dialog, DialogActions, DialogContent, DialogTitle, IconButton, Stack, TextField, Tooltip, Typography } from '@mui/material';
import ThumbDownIcon from '@mui/icons-material/ThumbDown';
import ThumbDownOutlinedIcon from '@mui/icons-material/ThumbDownOutlined';
import ThumbUpIcon from '@mui/icons-material/ThumbUp';
import ThumbUpOutlinedIcon from '@mui/icons-material/ThumbUpOutlined';
import { FEEDBACK_COMMENT_MAX_CHARS, type ChatFeedbackRating } from '@hv/shared';
import { describeError } from '../components/Notify';
import { useRateMessage } from './hooks';

export const FEEDBACK_NOTICE = 'The question and answer will be visible to administrators.';

export function ChatFeedback({ messageId, rating }: { messageId: string; rating: ChatFeedbackRating | null }) {
  const rate = useRateMessage();
  const [open, setOpen] = useState(false);
  const [comment, setComment] = useState('');

  const submit = (next: ChatFeedbackRating, text?: string) =>
    rate.mutate(
      { messageId, rating: next, comment: text?.trim() || undefined },
      {
        onSuccess: () => setOpen(false),
        onError: (err) => console.error('Saving assistant feedback failed', { messageId, rating: next, err }),
      },
    );

  const openDialog = () => {
    rate.reset();
    setComment('');
    setOpen(true);
  };

  return (
    <>
      <Stack direction="row" alignItems="center" gap={0.25} role="group" aria-label="Rate this answer" sx={{ mt: -1, mb: 1.5 }}>
        <Tooltip title="Helpful">
          <span>
            <IconButton size="small" aria-label="Helpful" aria-pressed={rating === 'up'} disabled={rate.isPending} onClick={() => rating !== 'up' && submit('up')}>
              {rating === 'up' ? <ThumbUpIcon fontSize="inherit" color="primary" /> : <ThumbUpOutlinedIcon fontSize="inherit" />}
            </IconButton>
          </span>
        </Tooltip>
        <Tooltip title="Not helpful">
          <span>
            <IconButton size="small" aria-label="Not helpful" aria-pressed={rating === 'down'} disabled={rate.isPending} onClick={openDialog}>
              {rating === 'down' ? <ThumbDownIcon fontSize="inherit" color="primary" /> : <ThumbDownOutlinedIcon fontSize="inherit" />}
            </IconButton>
          </span>
        </Tooltip>
        {rate.error && !open && (
          <Typography variant="caption" color="error" role="alert">
            Could not save your rating: {describeError(rate.error)}
          </Typography>
        )}
      </Stack>
      <Dialog open={open} onClose={() => !rate.isPending && setOpen(false)} aria-labelledby={`feedback-title-${messageId}`} maxWidth="xs" fullWidth>
        <DialogTitle id={`feedback-title-${messageId}`}>What was wrong with this answer?</DialogTitle>
        <DialogContent>
          <Stack gap={2} sx={{ pt: 0.5 }}>
            <Alert severity="info">{FEEDBACK_NOTICE}</Alert>
            <TextField
              label="Comment (optional)"
              multiline
              minRows={3}
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              slotProps={{ htmlInput: { maxLength: FEEDBACK_COMMENT_MAX_CHARS } }}
            />
            {rate.error && <Alert severity="error">Could not send your feedback: {describeError(rate.error)}</Alert>}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)} disabled={rate.isPending}>
            Cancel
          </Button>
          <Button variant="contained" disabled={rate.isPending} onClick={() => submit('down', comment)}>
            {rate.isPending ? 'Sending…' : 'Send feedback'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
